import { FC, useMemo } from "react";

import { useUseCaseLabels } from "@/components/studies/useCaseRequirements";
import { useLocalization } from "@/hooks";
import { QuestionResults, Section } from "@/types/resources";
import PackagingReport, { PackagingReportData } from "./PackagingReport";
import { cookTopBoxScoring } from "./_topBoxScoring";

interface PackagingSectionResultsProps {
  data: QuestionResults[];
  section: Section;
}

/* ── Cooker ─────────────────────────────────────────────── */

/** Templated packaging sections carry appeal / shelf intent / brand
    fit in a fixed order. Translated labels live here; pack names
    come from `section.stimuli`. */
const PackagingSectionResults: FC<PackagingSectionResultsProps> = ({
  data,
  section,
}) => {
  const { i18n } = useLocalization();
  const labels = useUseCaseLabels(section.use_case);

  const reportData: PackagingReportData = useMemo(() => {
    const metricLabels = [
      i18n.t("useCasePage.packaging.sampleReport.metrics.appeal"),
      i18n.t("useCasePage.packaging.sampleReport.metrics.shelfIntent"),
      i18n.t("useCasePage.packaging.sampleReport.metrics.brandFit"),
    ];
    const scored = cookTopBoxScoring(data, section, metricLabels, (i) =>
      labels.stimulusPlaceholder(i + 1)
    );
    return {
      metrics: scored.metrics,
      packs: scored.entities,
      perCellSampleSize: scored.perCellSampleSize,
      respondentCount: scored.respondentCount,
      topBoxCounts: scored.topBoxCounts,
    };
  }, [data, section, i18n, labels]);

  return <PackagingReport data={reportData} />;
};

export default PackagingSectionResults;
